import { jwtDecode } from 'jwt-decode';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AuthState, JWTPayloadType, LoginResult, MerchantAttachedPayload, User, UserLoadedPayload } from 'model/types/auth';

import { merchantAttachStarted, sessionEnded } from 'app/actions/sessionLifecycle';

import { authenticatedPost } from 'utils/axios';

import { USER_LOGIN } from 'queries/login';
import { GET_USER_INITIAL_INFO } from 'queries/get_user_initial_info';

export const initialState: AuthState = {
  phase: 'booting',
  isLoggedIn: false,
  flags: [],
  user: null,
  userTokenInfo: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<UserLoadedPayload>) {
      state.flags = action.payload.flags;
      state.isLoggedIn = !!action.payload.user;
      state.phase = 'idle';
      state.user = action.payload.user;
      state.userTokenInfo = decodeSessionToken(action.payload.token);
    },
    bootSettled(state) {
      state.phase = 'idle';
    },
    merchantAttached(state, action: PayloadAction<MerchantAttachedPayload>) {
      state.phase = 'idle';
      state.userTokenInfo = decodeSessionToken(action.payload.token);
    }
  },
  extraReducers: (builder) => {
    builder
      // An ended session has already booted, so it settles as idle rather than booting again.
      .addCase(sessionEnded, () => ({ ...initialState, phase: 'idle' }))
      // The keep list: anything not named here is discarded by the switch. The token payload is not on it,
      // because the attach issues a new one.
      .addCase(merchantAttachStarted, (state) => ({
        ...initialState,
        flags: state.flags,
        isLoggedIn: state.isLoggedIn,
        phase: 'attaching',
        user: state.user
      }));
  }
});

export const { bootSettled, merchantAttached, setUser } = authSlice.actions;

export async function userLogin(email: string, password: string): Promise<LoginResult> {
  const response = await authenticatedPost({
    query: USER_LOGIN,
    vars: {
      email,
      password
    }
  });
  if (response.data.login) {
    return { token: response.data.login.token };
  }
  return { token: null, errors: response.errors };
}

export async function queryUser(): Promise<AuthState> {
  const response = await authenticatedPost({
    query: GET_USER_INITIAL_INFO
  });
  let user: User | null = null;
  let flags: string[] = [];
  if (response.data?.me) {
    user = response.data.me;
  }
  if (response.data?.waffle?.flags) {
    flags = response.data.waffle.flags.filter((flag: any) => flag.isActive).map((flag: any) => flag.name);
  }
  return { flags, user };
}

export function decodeSessionToken(token?: string | null): JWTPayloadType | null {
  return token ? jwtDecode<JWTPayloadType>(token) : null;
}
